import {Button, Spinner, Typography} from "@material-tailwind/react";
import {useCallback, useEffect, useState} from "react";
import {toast} from "react-toastify";
import {
  getAgentProfileChangeRequests,
  withdrawAgentProfileChangeItem,
} from "../../../services/agent/agent_profile";
import {
  AGENT_PROFILE_FIELDS,
  CHANGE_STATUS,
  formatAgentProfileDate,
} from "./agentProfileModel";

const statusBadge = status => {
  const info = CHANGE_STATUS[status] ?? {label: status, className: "bg-slate-100 text-slate-700 border-slate-200"};
  return <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${info.className}`}>{info.label}</span>;
};

export function AgentProfileHistory({refreshSignal, onChanged}) {
  const [result, setResult] = useState({items: [], total: 0, page: 1, limit: 10});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const response = await getAgentProfileChangeRequests({page, limit: 10});
    if (response?.status === 200) {
      setResult(response.data);
    }
    setLoading(false);
  }, [page]);

  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  const withdraw = async itemId => {
    if (!window.confirm("این تغییر از صف بررسی خارج شود؟")) return;
    setWithdrawing(itemId);
    const response = await withdrawAgentProfileChangeItem(itemId);
    setWithdrawing(null);
    if (response?.status === 200) {
      toast.success("تغییر در انتظار بررسی لغو شد.");
      await load();
      onChanged?.();
      return;
    }
    toast.error("لغو تغییر انجام نشد. وضعیت پروفایل را دوباره بررسی کنید.");
  };

  const pageCount = Math.max(1, Math.ceil(result.total / result.limit));

  return (
    <section className="rounded-2xl border border-app-border bg-app-raised p-4 sm:p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Typography variant="h6" className="font-bold text-app-text">تاریخچه درخواست‌ها</Typography>
          <Typography className="mt-1 text-sm text-app-text-muted">نتیجه بررسی هر بخش جداگانه ثبت می‌شود.</Typography>
        </div>
        <span className="rounded-full bg-app-muted px-3 py-1 text-sm text-app-text-muted">{result.total ?? 0} درخواست</span>
      </div>

      {loading ? (
        <div className="flex min-h-28 items-center justify-center"><Spinner className="h-5 w-5"/></div>
      ) : result.items?.length ? (
        <div className="space-y-3">
          {result.items.map(request => (
            <article key={request.id} className="rounded-xl border border-app-border bg-app-surface p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-app-border pb-3">
                <Typography className="font-semibold text-app-text">درخواست شماره {request.id}</Typography>
                <Typography className="text-xs text-app-text-muted">{formatAgentProfileDate(request.submitted_at)}</Typography>
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                {(request.items ?? []).map(item => (
                  <div key={item.id} className="flex min-h-12 flex-wrap items-center justify-between gap-2 rounded-lg bg-app-muted/50 px-3 py-2">
                    <div>
                      <Typography className="text-sm font-medium text-app-text">{AGENT_PROFILE_FIELDS[item.field]?.label ?? item.field}</Typography>
                      {item.decision_reason && <Typography className="mt-1 text-xs text-rose-700">{item.decision_reason}</Typography>}
                    </div>
                    <div className="flex items-center gap-2">
                      {statusBadge(item.status)}
                      {item.status === "pending" && (
                        <Button
                          size="sm"
                          variant="text"
                          className="px-2 text-rose-700"
                          disabled={withdrawing === item.id}
                          onClick={() => withdraw(item.id)}
                        >
                          {withdrawing === item.id ? "در حال لغو..." : "لغو"}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-app-border p-7 text-center text-sm text-app-text-muted">هنوز درخواستی ثبت نشده است.</div>
      )}

      {pageCount > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button size="sm" variant="outlined" disabled={loading || result.page <= 1} onClick={() => setPage(result.page - 1)}>صفحه قبل</Button>
          <Typography className="text-sm text-app-text-muted">{result.page} از {pageCount}</Typography>
          <Button size="sm" variant="outlined" disabled={loading || result.page >= pageCount} onClick={() => setPage(result.page + 1)}>صفحه بعد</Button>
        </div>
      )}
    </section>
  );
}
